import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Avatar, SkeletonPage } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';
import { useAuthMe, useMeta } from '@/hooks';
import { useRequests } from '@/hooks';
import type { Meta, MenuKey, PlatformRole } from '@/api/types';
import { ApiHttpError, getErrorMessage } from '@/api';
import { logout } from '@/api/auth';
import { emailLocal } from '@/lib/format';

import styles from './Layout.module.css';

// Meta를 자식 페이지에서 어디서든 꺼낼 수 있도록 context로 노출.
const MetaContext = createContext<Meta | null>(null);

export function useMetaContext(): Meta {
  const ctx = useContext(MetaContext);
  if (!ctx) {
    throw new Error('useMetaContext: Layout 외부에서 호출됨');
  }
  return ctx;
}

interface BreadcrumbItem {
  label: string;
  to?: string;
}

// 라우트별 breadcrumb 빌더. orbit Topbar 동작 보존.
function useBreadcrumbs(): BreadcrumbItem[] {
  const { pathname } = useLocation();
  const params = useParams();
  return useMemo(() => {
    if (pathname === '/' || pathname === '/projects') {
      return [{ label: '프로젝트 관리' }];
    }
    if (pathname === '/projects/new') {
      return [
        { label: '프로젝트 관리', to: '/' },
        { label: '새 프로젝트 요청' },
      ];
    }
    if (pathname.startsWith('/projects/')) {
      const prefix = params.prefix ?? '';
      return [
        { label: '프로젝트 관리', to: '/' },
        { label: prefix || '프로젝트 상세' },
      ];
    }
    if (pathname === '/cpe') {
      return [{ label: 'Growth Tools' }, { label: 'CPE 캠페인 계산기' }];
    }
    if (pathname === '/members') {
      return [{ label: '멤버별 권한' }];
    }
    if (pathname === '/inbox') {
      return [{ label: '요청함' }];
    }
    if (pathname.startsWith('/inbox/')) {
      return [
        { label: '요청함', to: '/inbox' },
        { label: '요청 상세' },
      ];
    }
    if (pathname === '/admin') {
      return [{ label: '관리자', to: '/admin' }, { label: '사용자 관리' }];
    }
    if (pathname.startsWith('/catalog')) {
      return [{ label: '데이터 카탈로그' }];
    }
    if (pathname === '/api-tokens') {
      return [{ label: 'API' }, { label: 'API 토큰' }];
    }
    if (pathname === '/api-guide') {
      return [{ label: 'API' }, { label: 'API 가이드' }];
    }
    if (pathname.startsWith('/monitoring/bq-slots')) {
      return [{ label: '인프라 모니터', to: '/monitoring' }, { label: '빅쿼리 슬롯 모니터링' }];
    }
    if (pathname.startsWith('/monitoring')) {
      return [{ label: '인프라 모니터' }, { label: '서비스 모니터링' }];
    }
    if (pathname.startsWith('/bq-assets')) {
      return [{ label: '데이터 카탈로그', to: '/catalog' }, { label: 'BigQuery 미사용 테이블 관리' }];
    }
    if (pathname.startsWith('/growth/max-instance-control')) {
      return [{ label: 'Growth Tools' }, { label: 'MAX 긴급 차단' }];
    }
    if (pathname.startsWith('/networks/new')) {
      return [
        { label: '네트워크 관리', to: '/networks' },
        { label: 'Admon 네트워크', to: '/networks' },
        { label: '새 네트워크 요청' },
      ];
    }
    if (pathname.startsWith('/networks/resellers')) {
      return [{ label: '네트워크 관리' }, { label: '리셀러 허용' }];
    }
    if (pathname.startsWith('/networks')) {
      return [{ label: '네트워크 관리' }, { label: 'Admon 네트워크' }];
    }
    if (pathname.startsWith('/costs/all-projects')) {
      return [{ label: '비용 대시보드', to: '/costs/all-projects' }, { label: '전체 프로젝트' }];
    }
    if (pathname.startsWith('/costs/users')) {
      return [{ label: '비용 대시보드', to: '/costs/all-projects' }, { label: '유저별 비용' }];
    }
    if (pathname.startsWith('/costs/settings')) {
      return [{ label: '비용 대시보드', to: '/costs/all-projects' }, { label: '비용 설정' }];
    }
    return [{ label: '페이지' }];
  }, [pathname, params]);
}

interface LinkConfig {
  to: string;
  label: string;
  isActive: (pathname: string) => boolean;
  // platform_role 별 노출
  // PlatformRole 전체를 허용(런타임은 배열에 담긴 값으로만 매칭 — 'leader'는 미포함이라 동작 불변).
  visibleFor: PlatformRole[];
}

const PROJECT_LINKS: LinkConfig[] = [
  {
    to: '/',
    label: '프로젝트 목록',
    isActive: (p) =>
      p === '/' || p === '/projects' || p.startsWith('/projects/'),
    visibleFor: ['admin', 'leader', 'member', 'none'],
  },
  {
    to: '/members',
    label: '멤버별 권한',
    // 로그인한 사용자 모두 노출 — 비admin(grant 없는 none 포함)도 권한 부여 요청을 하기 때문.
    isActive: (p) => p === '/members',
    visibleFor: ['admin', 'leader', 'member', 'none'],
  },
];

export default function Layout() {
  // 1단계: 세션 쿠키 검증. 401 이면 /login 으로 redirect.
  const authState = useAuthMe();
  const location = useLocation();
  // 2단계: meta. 세션이 확인된 뒤에만 의미 있다.
  const meta = useMeta();

  if (authState.loading) {
    return <div className={styles.loading}>인증 확인 중...</div>;
  }

  if (authState.error) {
    const is401 = authState.error instanceof ApiHttpError && authState.error.status === 401;
    if (is401) {
      // 현재 경로를 return_to 로 전달 — 로그인 후 다시 돌아오도록.
      const ret = encodeURIComponent(location.pathname + location.search);
      return <Navigate to={`/login?return_to=${ret}`} replace />;
    }
    return (
      <div className={styles.shell}>
        <div className={styles.errorBanner}>
          인증 확인 실패: {getErrorMessage(authState.error)}
        </div>
      </div>
    );
  }

  if (meta.loading) {
    // 부트스트랩(meta) 대기 — 텍스트 대신 페이지 스켈레톤. (규칙: orbit-loading-skeleton 스킬)
    return (
      <div className={styles.loading}>
        <SkeletonPage />
      </div>
    );
  }

  if (meta.error || !meta.data) {
    return (
      <div className={styles.shell}>
        <div className={styles.errorBanner}>
          부트스트랩에 실패했어요: {meta.error ? getErrorMessage(meta.error) : 'meta 응답 없음'}
        </div>
      </div>
    );
  }

  // Guest(승인 전)·접근 메뉴 없는 역할만 가진 사용자 — 앱 대신 승인 대기 화면.
  // 서버 /api/meta 의 is_guest(menu_levels 비어 있음)와 같은 기준이라, 데이터가 비워진 빈 셸을 보여 주지 않는다.
  const currentUser = meta.data.current_user;
  if (
    currentUser.platform_role === 'none'
    || (currentUser.platform_role !== 'admin' && Object.keys(currentUser.menu_levels ?? {}).length === 0)
  ) {
    return <PendingApproval email={meta.data.current_user.email} />;
  }

  return (
    <MetaContext.Provider value={meta.data}>
      <Shell meta={meta.data} />
    </MetaContext.Provider>
  );
}

// 가입 승인 대기 화면 — Guest 역할 사용자에게 앱 대신 표시.
function PendingApproval({ email }: { email: string | null }) {
  return (
    <div className={styles.pendingWrap}>
      <div className={styles.pendingCard}>
        <div className={styles.pendingIcon}>⏳</div>
        <div className={styles.pendingTitle}>가입 승인 대기 중</div>
        <div className={styles.pendingBody}>
          {email && <b>{email}</b>} 계정의 가입 요청이 요청함에 접수되었고 관리자에게 알림이 갔어요.
          <br />
          관리자가 요청함에서 승인하면 바로 서비스를 이용할 수 있어요.
          <br />
          급하신 경우 데이터 플랫폼팀에 문의해 주세요.
        </div>
        <button
          type="button"
          className={styles.pendingLogout}
          onClick={() => {
            void logout().finally(() => {
              window.location.href = '/login';
            });
          }}
        >
          다른 계정으로 로그인
        </button>
      </div>
    </div>
  );
}

function Shell({ meta }: { meta: Meta }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const crumbs = useBreadcrumbs();
  const role = meta.current_user.platform_role;
  const isAdmin = role === 'admin';
  // 메뉴별 세분화 권한 — admin이 아니어도 역할 매핑(access 이상)으로 메뉴 노출.
  const menuLevels = meta.current_user.menu_levels ?? {};

  // 대기 중 요청 카운트 — admin만. member에게는 본인 요청 카운트(아직 별도 API 없으므로 미표시).
  const pendingReqs = useRequests(
    isAdmin ? { status: 'pending' } : { status: 'pending', mine: true },
  );
  const pendingCount = pendingReqs.data?.requests.length ?? 0;

  const inProjects = PROJECT_LINKS[0].isActive(pathname);
  const inMembers = pathname === '/members';
  const inInbox = pathname === '/inbox' || pathname.startsWith('/inbox/');
  const inAdmin = pathname === '/admin';
  const inCatalog = pathname.startsWith('/catalog');
  const inCosts = pathname.startsWith('/costs');
  const inMonitoring = pathname.startsWith('/monitoring');
  const inBqAssets = pathname.startsWith('/bq-assets');
  const inApi = pathname === '/api-tokens' || pathname === '/api-guide';
  const inNetworkMeta = pathname.startsWith('/networks');
  const inResellerAllowed = pathname.startsWith('/networks/resellers');
  const inMaxControl = pathname.startsWith('/growth/max-instance-control');

  // platform_role 별로 노출할 메뉴 결정.
  const showMembers = PROJECT_LINKS[1].visibleFor.includes(role);
  // 로그인한 사용자는 모두 요청함 노출 — 본인 요청 조회 + 요청 흐름(none 포함).
  const showInbox = true;

  // 사이드 메뉴 아코디언 — 열린 그룹은 localStorage 에 기억, 현재 경로가 속한 그룹은 자동으로 펼친다.
  const activeGroup = inProjects || inMembers ? 'projects'
    : inCatalog || inBqAssets ? 'catalog'
    : inCosts ? 'costs'
    : inNetworkMeta ? 'networks'
    : pathname === '/cpe' || inMaxControl ? 'growth'
    : inMonitoring ? 'monitoring'
    : inApi ? 'api'
    : null;
  const { openGroups, toggleGroup } = useNavGroups(activeGroup);
  const { scrollRef, scrolling } = useScrollIndicator();
  const { collapsed, toggleCollapsed, peek, peekHandlers } = useSidebarCollapsed();
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 900px)').matches);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const sidebarCollapsed = narrow ? !mobileNavOpen : collapsed;
  const toggleNavigation = () => narrow ? setMobileNavOpen(value => !value) : toggleCollapsed();
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 900px)');
    const change = () => { setNarrow(media.matches); setMobileNavOpen(false); };
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  useEffect(() => setMobileNavOpen(false), [pathname]);
  useEffect(() => {
    if (!narrow || !mobileNavOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const main = mainRef.current;
    if (main) main.inert = true;
    const sidebar = document.getElementById('orbit-sidebar');
    const focusable = () => Array.from(sidebar?.querySelectorAll<HTMLElement>('button, a[href]') || []).filter(el => el.getClientRects().length > 0);
    focusable()[0]?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
      if (event.key !== 'Tab') return;
      const entries = focusable();
      if (event.shiftKey && document.activeElement === entries[0]) { event.preventDefault(); entries.at(-1)?.focus(); }
      else if (!event.shiftKey && document.activeElement === entries.at(-1)) { event.preventDefault(); entries[0]?.focus(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      if (main) main.inert = false;
      document.removeEventListener('keydown', handleKey);
      (main?.querySelector<HTMLElement>('button[aria-controls="orbit-sidebar"]') || previous)?.focus();
    };
  }, [narrow, mobileNavOpen]);

  return (
    <div className={styles.shell} style={{ '--orbit-sidebar-offset': sidebarCollapsed ? '0px' : '248px' } as CSSProperties}>
      {narrow && mobileNavOpen && <button className={styles.mobileBackdrop} aria-label="메뉴 닫기" onClick={() => setMobileNavOpen(false)} />}
      {!narrow && collapsed && <div className={styles.peekZone} aria-hidden="true" {...peekHandlers} />}
      <aside
        id="orbit-sidebar"
        role={narrow && mobileNavOpen ? 'dialog' : undefined}
        aria-modal={narrow && mobileNavOpen ? true : undefined}
        aria-label={narrow ? 'Orbit 메뉴' : undefined}
        className={`${styles.sidebar} ${sidebarCollapsed ? styles.sidebarHidden : ''} ${!narrow && peek ? styles.sidebarPeek : ''} ${narrow && mobileNavOpen ? styles.mobileSidebar : ''}`}
        {...(!narrow && collapsed ? peekHandlers : {})}
      >
        <div className={styles.brand}>
          <div className={styles.brandLogo}>S</div>
          <div className={styles.brandText}>
            <div className={styles.brandName}>Supercent</div>
            <div className={styles.brandSub}>데이터 플랫폼팀</div>
          </div>
          {/* 펼친 상태: ‹ 메뉴 감추기 (상단바 왼쪽 › 로 다시 펼침, Notion 식).
              peek 중(감춘 상태): › 메뉴 고정 — 클릭하면 펼친 상태로 고정된다. peek 닫기는 마우스를 떼면 된다. */}
          <button
            type="button"
            className={styles.navToggle}
            aria-label={narrow ? '메뉴 닫기' : collapsed ? '메뉴 고정' : '메뉴 감추기'}
            title={narrow ? '메뉴 닫기' : collapsed ? '메뉴 고정' : '메뉴 감추기'}
            onClick={toggleNavigation}
          >
            <NavIcon name={collapsed ? 'chev-r' : 'chev-l'} size={14} color="#9B9B9B" />
          </button>
        </div>

        <div
          ref={scrollRef}
          className={`${styles.navScroll} ${scrolling ? styles.navScrolling : ''}`}
        >
        <div className={styles.sectionLabel}>WORKSPACE</div>
        <NavGroup id="projects" label="프로젝트 관리" active={inProjects} openGroups={openGroups} onToggle={toggleGroup}>
            <Link
              to="/"
              className={`${styles.link} ${inProjects && !inMembers ? styles.active : ''}`}
            >
              프로젝트 목록
            </Link>
            {showMembers && (
              <Link
                to="/members"
                className={`${styles.link} ${inMembers ? styles.active : ''}`}
              >
                멤버별 권한
              </Link>
            )}
        </NavGroup>

        {/* 데이터 카탈로그 — 카탈로그는 기본 개방, BQ 미사용 테이블은 소메뉴 키(bq_assets)로 게이팅. */}
        {(
          <NavGroup id="catalog" label="데이터 카탈로그" active={inCatalog || inBqAssets} openGroups={openGroups} onToggle={toggleGroup}>
              <Link to="/catalog" className={`${styles.link} ${inCatalog ? styles.active : ''}`}>
                카탈로그
              </Link>
              {(isAdmin || !!menuLevels.bq_assets) && (
                <Link to="/bq-assets" className={`${styles.link} ${inBqAssets ? styles.active : ''}`}>
                  BigQuery 미사용 테이블 관리
                </Link>
              )}
          </NavGroup>
        )}

        {/* 비용 대시보드 — 소메뉴 키(costs/costs_users)별 노출 */}
        {(isAdmin || !!menuLevels.costs || !!menuLevels.costs_users) && (
          <NavGroup id="costs" label="비용 대시보드" active={inCosts} openGroups={openGroups} onToggle={toggleGroup}>
              {(isAdmin || !!menuLevels.costs) && (
                <Link
                  to="/costs/all-projects"
                  className={`${styles.link} ${pathname === '/costs/all-projects' ? styles.active : ''}`}
                >
                  전체 프로젝트
                </Link>
              )}
              {(isAdmin || !!menuLevels.costs_users) && (
                <Link
                  to="/costs/users"
                  className={`${styles.link} ${pathname === '/costs/users' ? styles.active : ''}`}
                >
                  유저별 비용
                </Link>
              )}
              {/* '비용 설정' 메뉴는 숨김 (사용자 결정) — 라우트(/costs/settings)는 직접 URL로 접근 가능 */}
          </NavGroup>
        )}

        {/* 네트워크 관리 — 네트워크 메타데이터·리셀러 허용. 소메뉴 키(network_meta/reseller_allowed)별 노출. */}
        {(isAdmin || !!menuLevels.network_meta || !!menuLevels.reseller_allowed) && (
          <NavGroup id="networks" label="네트워크 관리" active={inNetworkMeta} openGroups={openGroups} onToggle={toggleGroup}>
              {(isAdmin || !!menuLevels.network_meta) && (
                <Link
                  to="/networks"
                  className={`${styles.link} ${inNetworkMeta && !inResellerAllowed ? styles.active : ''}`}
                >
                  Admon 네트워크
                </Link>
              )}
              {(isAdmin || !!menuLevels.reseller_allowed) && (
                <Link
                  to="/networks/resellers"
                  className={`${styles.link} ${inResellerAllowed ? styles.active : ''}`}
                >
                  리셀러 허용
                </Link>
              )}
          </NavGroup>
        )}

        {/* 메뉴 이동은 기존 접근 범위를 넓히지 않는다. 도구별 기존 메뉴 게이트를 유지한다. */}
        {(isAdmin || !!menuLevels.projects || !!menuLevels.network_meta) && (
          <NavGroup id="growth" label="Growth Tools" active={pathname === '/cpe' || inMaxControl} openGroups={openGroups} onToggle={toggleGroup}>
            {(isAdmin || !!menuLevels.projects) && (
              <Link to="/cpe" className={`${styles.link} ${pathname === '/cpe' ? styles.active : ''}`}>
                CPE 캠페인 계산기
              </Link>
            )}
            {(isAdmin || !!menuLevels.network_meta) && (
              <Link to="/growth/max-instance-control" className={`${styles.link} ${inMaxControl ? styles.active : ''}`}>
                MAX 긴급 차단
              </Link>
            )}
          </NavGroup>
        )}

        {/* 인프라 모니터 — 소메뉴 키(monitoring/monitoring_bq_slots)별 노출 */}
        {(isAdmin || !!menuLevels.monitoring || !!menuLevels.monitoring_bq_slots) && (
          <NavGroup id="monitoring" label="인프라 모니터" active={inMonitoring} openGroups={openGroups} onToggle={toggleGroup}>
              {(isAdmin || !!menuLevels.monitoring) && (
                <Link
                  to="/monitoring"
                  className={`${styles.link} ${pathname === '/monitoring' ? styles.active : ''}`}
                >
                  서비스 모니터링
                </Link>
              )}
              {(isAdmin || !!menuLevels.monitoring_bq_slots) && (
                <Link
                  to="/monitoring/bq-slots"
                  className={`${styles.link} ${pathname === '/monitoring/bq-slots' ? styles.active : ''}`}
                >
                  빅쿼리 슬롯 모니터링
                </Link>
              )}
          </NavGroup>
        )}

        {/* API — 토큰 관리 / 가이드 (소메뉴 키 api_tokens/api_guide) */}
        {(isAdmin || !!menuLevels.api_tokens || !!menuLevels.api_guide) && (
          <NavGroup id="api" label="API" active={inApi} openGroups={openGroups} onToggle={toggleGroup}>
              {(isAdmin || !!menuLevels.api_tokens) && (
                <Link to="/api-tokens" className={`${styles.link} ${pathname === '/api-tokens' ? styles.active : ''}`}>
                  API 토큰
                </Link>
              )}
              {(isAdmin || !!menuLevels.api_guide) && (
                <Link to="/api-guide" className={`${styles.link} ${pathname === '/api-guide' ? styles.active : ''}`}>
                  API 가이드
                </Link>
              )}
          </NavGroup>
        )}

        </div>

        {showInbox && (
          <div className={styles.inboxBlock}>
            <button
              type="button"
              className={`${styles.inboxItem} ${inInbox ? styles.active : ''}`}
              onClick={() => navigate('/inbox')}
            >
              <NavIcon name="inbox" size={16} color={inInbox ? '#fff' : '#5C5C5C'} />
              <span style={{ flex: 1 }}>요청함</span>
              {pendingCount > 0 && <span className={styles.inboxBadge}>{pendingCount}</span>}
            </button>
          </div>
        )}

        <div className={styles.userBlock}>
          <button
            type="button"
            className={`${styles.userBtn} ${inAdmin ? styles.active : ''} ${isAdmin ? '' : styles.disabled}`}
            onClick={isAdmin ? () => navigate('/admin') : undefined}
            title={isAdmin ? '관리자 페이지' : undefined}
          >
            <Avatar
              email={meta.current_user.email}
              name={emailLocal(meta.current_user.email)}
              size={32}
            />
            <div className={styles.userInfo}>
              <div className={styles.userName}>{emailLocal(meta.current_user.email)}</div>
              <div className={styles.userMeta}>
                {role === 'admin' ? 'Admin' : role === 'leader' ? 'Leader' : role === 'member' ? 'Member' : '권한 없음'}
                {isAdmin && <span className={styles.adminBadge}>관리자</span>}
              </div>
            </div>
            {isAdmin && <NavIcon name="chev-r" color="#C4C4C4" size={12} />}
          </button>
          <button
            className={styles.iconBtn}
            type="button"
            aria-label="로그아웃"
            title="로그아웃"
            onClick={async () => {
              try {
                await logout();
              } catch {
                // 쿠키만 못 지워도 /login 으로 가면 backend가 다시 401을 줄 것.
              }
              window.location.href = '/login';
            }}
          >
            <NavIcon name="logout" color="#9B9B9B" size={14} />
          </button>
        </div>
      </aside>

      <main className={styles.main} ref={mainRef}>
        <div className={styles.topbar}>
          <div className={styles.topbarLeft}>
            {sidebarCollapsed && (
              <button
                type="button"
                className={`${styles.navToggle} ${styles.navExpand}`}
                aria-label="메뉴 펼치기"
                aria-expanded={narrow ? mobileNavOpen : peek}
                aria-controls="orbit-sidebar"
                title="메뉴 펼치기"
                onClick={toggleNavigation}
                {...(!narrow ? peekHandlers : {})}
              >
                <NavIcon name="chev-r" size={14} color="#5C5C5C" />
              </button>
            )}
            <div className={styles.crumbs}>
            {crumbs.map((c, i) => (
              <span key={`${c.label}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {i > 0 && <NavIcon name="chev-r" color="#C4C4C4" size={10} />}
                {c.to && i < crumbs.length - 1 ? (
                  <button
                    type="button"
                    className={styles.crumb}
                    onClick={() => navigate(c.to!)}
                  >
                    {c.label}
                  </button>
                ) : (
                  <span className={styles.crumbCurrent}>{c.label}</span>
                )}
              </span>
            ))}
            </div>
          </div>
        </div>
        <div className={styles.scrollArea} id="orbit-content-scroll">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

// 보호된 페이지: platform_role 체크. 통과 못하면 안내 화면.
interface ProtectedProps {
  allow: PlatformRole[];
  // 지정 시 메뉴 레벨(access 이상)로도 통과 — 역할 매핑으로 열어준 메뉴 접근용.
  menu?: MenuKey;
  children: ReactNode;
  reason?: string;
}

export function Protected({ allow, children, reason, menu }: ProtectedProps) {
  const meta = useMetaContext();
  const byMenu = menu ? !!(meta.current_user.menu_levels ?? {})[menu] : false;
  if (allow.includes(meta.current_user.platform_role) || byMenu) {
    return <>{children}</>;
  }
  return (
    <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
      <div style={{ maxWidth: 360, textAlign: 'center' }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            background: 'var(--sc-bg-muted, #F4F4F4)',
            margin: '0 auto 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#9B9B9B" strokeWidth="1.8">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <div style={{ font: '700 16px/1.3 "Pretendard"' }}>접근 권한이 없어요</div>
        <div
          style={{
            font: '500 13px/1.5 "Pretendard"',
            color: 'var(--sc-text-muted)',
            marginTop: 6,
          }}
        >
          {reason ?? '이 페이지를 볼 권한이 필요합니다.'}
        </div>
      </div>
    </div>
  );
}


// ── 사이드 메뉴 아코디언 ───────────────────────────────────────────

const NAV_OPEN_KEY = 'orbit.nav.open';
const DEFAULT_OPEN_GROUPS = ['projects'];

function readOpenGroups(): string[] {
  try {
    const raw = window.localStorage.getItem(NAV_OPEN_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : DEFAULT_OPEN_GROUPS;
  } catch {
    return DEFAULT_OPEN_GROUPS;
  }
}

/** 열린 그룹 집합. 현재 경로가 속한 그룹은 항상 펼치고, 사용자가 접거나 편 상태는 브라우저에 기억한다. */
function useNavGroups(activeGroup: string | null) {
  const [openGroups, setOpenGroups] = useState<string[]>(readOpenGroups);
  useEffect(() => {
    if (activeGroup) {
      setOpenGroups((prev) => (prev.includes(activeGroup) ? prev : [...prev, activeGroup]));
    }
  }, [activeGroup]);
  useEffect(() => {
    try {
      window.localStorage.setItem(NAV_OPEN_KEY, JSON.stringify(openGroups));
    } catch {
      /* 저장 실패는 무시 — 다음 방문에 기본값으로 열린다 */
    }
  }, [openGroups]);
  const toggleGroup = (id: string) =>
    setOpenGroups((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));
  return { openGroups, toggleGroup };
}

const NAV_COLLAPSED_KEY = 'orbit.nav.collapsed';

// peek 열림·닫힘 지연(ms) — 스쳐 지나가는 마우스에 열리지 않고, 살짝 벗어나도 바로 닫히지 않게.
const PEEK_OPEN_DELAY = 120;
const PEEK_CLOSE_DELAY = 300;

/** 사이드 메뉴 전체 감춤 여부. 사이드바 헤더의 ‹ 로 감추고 상단바의 › 로 펼치며, 브라우저에 기억한다.
 *  감춘 상태에서는 왼쪽 끝 띠·› 버튼·사이드바 자체에 마우스를 올리면 잠깐 꺼내 보는 peek 이 열린다(Arc·GitLab 식, 저장 안 함). */
function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(NAV_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      /* 저장 실패는 무시 — 다음 방문에 메뉴가 펼쳐진 채로 열린다 */
    }
  }, [collapsed]);

  const [peek, setPeek] = useState(false);
  const peekTimer = useRef<number | undefined>(undefined);
  const clearPeekTimer = () => window.clearTimeout(peekTimer.current);
  useEffect(() => clearPeekTimer, []);
  const peekHandlers = {
    onMouseEnter: () => {
      clearPeekTimer();
      peekTimer.current = window.setTimeout(() => setPeek(true), PEEK_OPEN_DELAY);
    },
    onMouseLeave: () => {
      clearPeekTimer();
      peekTimer.current = window.setTimeout(() => setPeek(false), PEEK_CLOSE_DELAY);
    },
  };
  const toggleCollapsed = () => {
    clearPeekTimer();
    setPeek(false);
    setCollapsed((v) => !v);
  };
  return { collapsed, toggleCollapsed, peek, peekHandlers };
}

/** 스크롤 중일 때만 얇은 스크롤바를 보이기 위한 플래그 — 마지막 스크롤 후 0.8초 뒤 숨김. */
function useScrollIndicator() {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrolling, setScrolling] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let timer: number | undefined;
    const onScroll = () => {
      setScrolling(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setScrolling(false), 800);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.clearTimeout(timer);
    };
  }, []);
  return { scrollRef, scrolling };
}

interface NavGroupProps {
  id: string;
  label: string;
  active: boolean;
  openGroups: string[];
  onToggle: (id: string) => void;
  children: ReactNode;
}

function NavGroup({ id, label, active, openGroups, onToggle, children }: NavGroupProps) {
  const open = openGroups.includes(id);
  return (
    <nav className={styles.nav}>
      <button
        className={`${styles.group} ${active ? styles.groupCurrent : ''}`}
        type="button"
        aria-expanded={open}
        aria-controls={`nav-group-${id}`}
        onClick={() => onToggle(id)}
      >
        <NavIcon name="grid" size={16} color={active ? '#0B0B0B' : '#5C5C5C'} />
        <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
        <span className={`${styles.groupChevron} ${open ? styles.groupChevronOpen : ''}`}>
          <NavIcon name="chev-d" color="#9B9B9B" size={10} />
        </span>
      </button>
      <div id={`nav-group-${id}`} className={`${styles.groupBody} ${open ? styles.groupBodyOpen : ''}`}>
        <div className={styles.groupBodyInner}>
          <div className={styles.groupChildren}>{children}</div>
        </div>
      </div>
    </nav>
  );
}
